import { useCallback, useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  HiBars3,
  HiOutlineArrowPath,
  HiOutlineArrowRightCircle,
  HiOutlineGlobeAlt,
  HiOutlineLockClosed,
  HiOutlinePhoto,
  HiOutlineSquares2X2,
  HiOutlineXMark,
} from "react-icons/hi2";

import classes from "./SubNav.module.css";

const DESKTOP_QUERY = "(min-width: 64em)";

const SubNav = () => {
  const location = useLocation();
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia(DESKTOP_QUERY).matches
      : true
  );
  const [isOpen, setIsOpen] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia(DESKTOP_QUERY).matches
      : true
  );

  const close = useCallback(() => {
    if (!window.matchMedia(DESKTOP_QUERY).matches) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_QUERY);
    const sync = () => {
      const desktop = mq.matches;
      setIsDesktop(desktop);
      setIsOpen(desktop);
    };
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    close();
  }, [location.pathname, close]);

  useEffect(() => {
    if (isDesktop || !isOpen) {
      return undefined;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isDesktop, isOpen]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        close();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [close]);

  const linkClass = (navData) =>
    navData.isActive ? `${classes.link} ${classes.active}` : classes.link;

  const afterNav = close;

  const panelClassName = [
    classes.panel,
    !isDesktop && (isOpen ? classes.panelOpen : classes.panelClosed),
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={classes.root}>
      {!isDesktop && isOpen && (
        <button
          type="button"
          className={classes.backdrop}
          aria-label="Close account menu"
          onClick={close}
        />
      )}

      {!isDesktop && !isOpen && (
        <button
          type="button"
          className={classes.peekTab}
          aria-label="Open account menu"
          onClick={() => setIsOpen(true)}
        >
          <HiBars3 className={classes.peekIcon} aria-hidden />
          <span className={classes.peekText}>Menu</span>
        </button>
      )}

      <nav className={panelClassName} aria-label="Account">
        <div className={classes.panelHead}>
          <div>
            <p className={classes.eyebrow}>Your account</p>
            <p className={classes.headline}>Banking &amp; transfers</p>
          </div>
          <button
            type="button"
            className={classes.closeBtn}
            aria-label="Close menu"
            onClick={close}
          >
            <HiOutlineXMark aria-hidden />
          </button>
        </div>

        <div className={classes.links}>
          <NavLink to="detail" className={linkClass} onClick={afterNav}>
            <span className={classes.iconWrap}>
              <HiOutlineSquares2X2 className={classes.icon} aria-hidden />
            </span>
            <span className={classes.linkLabel}>Dashboard</span>
          </NavLink>
          <NavLink
            to="localTransfer"
            className={linkClass}
            onClick={afterNav}
          >
            <span className={classes.iconWrap}>
              <HiOutlineArrowRightCircle className={classes.icon} aria-hidden />
            </span>
            <span className={classes.linkLabel}>Domestic transfer</span>
          </NavLink>
          <NavLink
            to="internationalTransfer"
            className={linkClass}
            onClick={afterNav}
          >
            <span className={classes.iconWrap}>
              <HiOutlineGlobeAlt className={classes.icon} aria-hidden />
            </span>
            <span className={classes.linkLabel}>International transfer</span>
          </NavLink>
          <NavLink to="history" className={linkClass} onClick={afterNav}>
            <span className={classes.iconWrap}>
              <HiOutlineArrowPath className={classes.icon} aria-hidden />
            </span>
            <span className={classes.linkLabel}>Transaction history</span>
          </NavLink>
          <NavLink to="password" className={linkClass} onClick={afterNav}>
            <span className={classes.iconWrap}>
              <HiOutlineLockClosed className={classes.icon} aria-hidden />
            </span>
            <span className={classes.linkLabel}>Change password</span>
          </NavLink>
          <NavLink to="changePhoto" className={linkClass} onClick={afterNav}>
            <span className={classes.iconWrap}>
              <HiOutlinePhoto className={classes.icon} aria-hidden />
            </span>
            <span className={classes.linkLabel}>Change photo</span>
          </NavLink>
        </div>
      </nav>
    </div>
  );
};

export default SubNav;
